import { usePalette } from '@bravemove/design-tokens/react';
import { AppearanceIcon } from '@bravemove/design-tokens/icons';
import { useI18n } from '@bravemove/i18n/react/core';
import { cn } from '../../utils/cn';
import { PALETTE_KEYS, PALETTE_LABELS, writeStoredPalette, type PaletteKey } from './themeMode';

export interface ThemeToggleProps {
  readonly className?: string;
}

/**
 * Top-bar theme switch — a single icon button that CYCLES through all four
 * palettes (slate/garnet × light/dark) on each click. The choice is persisted
 * so it survives reloads; pair with `resolveInitialPalette()` in the app's
 * `main.tsx`. The glyph is the standard half-filled "appearance" contrast
 * circle, drawn on `currentColor` so it inherits the top-bar ink.
 */
export function ThemeToggle({ className }: ThemeToggleProps) {
  const { t } = useI18n();
  const { palette, setPalette } = usePalette();
  const currentIndex = Math.max(0, PALETTE_KEYS.indexOf(palette as PaletteKey));
  const next = PALETTE_KEYS[(currentIndex + 1) % PALETTE_KEYS.length]!;

  const cycle = (): void => {
    setPalette(next as Parameters<typeof setPalette>[0]);
    document.documentElement.style.colorScheme = next.endsWith('_dark') ? 'dark' : 'light';
    writeStoredPalette(next);
  };

  const currentLabel = t(`common:theme.names.${palette}`, {
    defaultValue: PALETTE_LABELS[palette as PaletteKey],
  });
  const nextLabel = t(`common:theme.names.${next}`, { defaultValue: PALETTE_LABELS[next] });
  const label = t('common:theme.action', {
    current: currentLabel,
    next: nextLabel,
    defaultValue: `Appearance: ${currentLabel}. Switch to ${nextLabel}.`,
  });

  return (
    <button
      type="button"
      onClick={cycle}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center size-10 [@media(pointer:coarse)]:size-11 rounded-md text-ink-2',
        'hover:bg-surface-2 hover:text-ink transition-colors duration-(--duration-tap) motion-reduce:transition-none',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text',
        className,
      )}
    >
      <AppearanceIcon size={20} color="currentColor" />
    </button>
  );
}
